import { useMemo } from 'react';
import { cn } from '@/lib/cn';
import type { Code99Event, Code99Rhythm } from '@/types';
import { useCode99Store } from './code99-store';
import {
  Zap, Activity, Pill, Heart, Stethoscope, ClipboardCheck,
  AlertTriangle, CheckCircle2, Circle, RotateCw, Skull, Plus,
} from 'lucide-react';

/**
 * ACLSAlgorithmPanel — Zone 3 live algorithm guide.
 *
 * Updates automatically based on the most recent 'rhythm' event in the
 * code99 event log. Four branches:
 *
 *   1. Shockable     (VF / PVT)   → Shock → 2 min CPR → Epi q3–5 → Shock →
 *                                   2 min CPR → Amiodarone → Shock → 2 min
 *                                   CPR → Reversible causes
 *   2. Non-Shockable (PEA / Asystole) → CPR → Epi q3–5 → 2 min CPR → reassess
 *                                        → reversible causes → continue
 *   3. ROSC          → 12-lead within 10 min → TTM consideration → vent
 *                      parameters (SpO2 94–99%, EtCO2 35–45) → MAP ≥65 →
 *                      transport
 *   4. Termination   → surfaces when asystole ≥ 20 min with adequate CPR,
 *                      reversible causes addressed
 *
 * Each step in the current branch is marked complete / current / upcoming
 * based on what the crew has logged. The H's & T's checklist is always
 * visible at the bottom of the panel.
 *
 * Design intent: for an inexperienced officer detailed into role, this is
 * a live cheat sheet that tells them what to do next. For a seasoned
 * paramedic, it's a reassurance + backup.
 */

interface Step {
  key: string;
  label: string;
  icon: typeof Zap;
  /** Has this step been "done" (based on event log)? */
  done: (events: Code99Event[]) => boolean;
  /** True if this is the next expected step when prior steps complete. */
  timing?: string;
}

// Helper predicates used by multiple branches
const hasAnyRhythm = (evs: Code99Event[]) => evs.some((e) => e.event_type === 'rhythm');
const firstCprStart = (evs: Code99Event[]) => evs.filter((e) => e.event_type === 'cpr_start').at(-1) ?? null;
const shockCount = (evs: Code99Event[]) => evs.filter((e) => e.event_type === 'shock').length;
const epiCount = (evs: Code99Event[]) =>
  evs.filter((e) => e.event_type === 'medication' &&
    typeof e.details?.name === 'string' &&
    (e.details.name as string).toLowerCase().startsWith('epi')).length;
const amioCount = (evs: Code99Event[]) =>
  evs.filter((e) => e.event_type === 'medication' &&
    typeof e.details?.name === 'string' &&
    (e.details.name as string).toLowerCase().startsWith('amio')).length;
const hasAirway = (evs: Code99Event[]) => evs.some((e) => e.event_type === 'airway');
const hasROSC = (evs: Code99Event[]) => evs.some((e) => e.event_type === 'rosc');
const has12Lead = (evs: Code99Event[]) => evs.some((e) => e.event_type === 'twelve_lead');

// ============================================================
// Branches
// ============================================================
const SHOCKABLE_BRANCH: Step[] = [
  { key: 'cpr_start',      label: 'High-quality CPR in progress',              icon: Activity, done: (e) => !!firstCprStart(e), timing: 'continuous' },
  { key: 'first_shock',    label: 'Defibrillate (1st shock)',                  icon: Zap,      done: (e) => shockCount(e) >= 1, timing: '200 J biphasic' },
  { key: 'cpr_round_1',    label: 'CPR × 2 min + establish IV/IO',             icon: Activity, done: (e) => shockCount(e) >= 1 && firstCprStart(e) !== null, timing: '2:00 cycle' },
  { key: 'epi_1',          label: 'Epinephrine 1 mg IV/IO',                    icon: Pill,     done: (e) => epiCount(e) >= 1, timing: 'q 3–5 min' },
  { key: 'second_shock',   label: 'Defibrillate (2nd shock)',                  icon: Zap,      done: (e) => shockCount(e) >= 2, timing: '200 J biphasic' },
  { key: 'cpr_round_2',    label: 'CPR × 2 min + consider advanced airway',    icon: Activity, done: (e) => shockCount(e) >= 2 && hasAirway(e), timing: '2:00 cycle' },
  { key: 'amiodarone_1',   label: 'Amiodarone 300 mg IV/IO',                   icon: Pill,     done: (e) => amioCount(e) >= 1, timing: 'or Lidocaine 1–1.5 mg/kg' },
  { key: 'third_shock',    label: 'Defibrillate (3rd shock)',                  icon: Zap,      done: (e) => shockCount(e) >= 3, timing: '200 J biphasic' },
  { key: 'cpr_round_3',    label: 'CPR × 2 min + treat reversible causes',     icon: Activity, done: (e) => shockCount(e) >= 3, timing: 'H\'s & T\'s' },
  { key: 'amiodarone_2',   label: 'Amiodarone 150 mg IV/IO (if indicated)',    icon: Pill,     done: (e) => amioCount(e) >= 2 },
];

const NON_SHOCKABLE_BRANCH: Step[] = [
  { key: 'cpr_start',      label: 'High-quality CPR in progress',              icon: Activity, done: (e) => !!firstCprStart(e), timing: 'continuous' },
  { key: 'iv_io',          label: 'Establish IV / IO access',                  icon: Activity, done: (e) => e.some((x) => x.event_type === 'iv_io' && x.details?.success) },
  { key: 'epi_1',          label: 'Epinephrine 1 mg IV/IO',                    icon: Pill,     done: (e) => epiCount(e) >= 1, timing: 'q 3–5 min' },
  { key: 'cpr_round_1',    label: 'CPR × 2 min, reassess rhythm',              icon: Activity, done: (e) => epiCount(e) >= 1 && e.filter((x) => x.event_type === 'rhythm').length >= 2 },
  { key: 'airway',         label: 'Advanced airway + waveform capnography',    icon: Stethoscope, done: hasAirway },
  { key: 'reversible',     label: 'Treat reversible causes (H\'s & T\'s)',     icon: AlertTriangle, done: (e) => e.some((x) => x.event_type === 'reversible_cause' && x.details?.status === 'addressed') },
  { key: 'epi_ongoing',    label: 'Continue Epi q 3–5 min',                    icon: Pill,     done: (e) => epiCount(e) >= 2, timing: 'cycle until ROSC or termination' },
];

const ROSC_BRANCH: Step[] = [
  { key: 'post_rosc_12lead', label: '12-lead ECG within 10 minutes',           icon: Heart,    done: (e) => has12Lead(e) && hasROSC(e), timing: 'STEMI detection → PCI destination' },
  { key: 'post_rosc_vitals', label: 'Post-ROSC vital set + SpO2 + EtCO2',      icon: Activity, done: (e) => e.filter((x) => x.event_type === 'rosc').length >= 1 },
  { key: 'airway_confirm',  label: 'Confirm airway + waveform capnography',    icon: Stethoscope, done: hasAirway, timing: 'target EtCO2 35–45' },
  { key: 'vent_target',     label: 'Target SpO2 94–99% (avoid hyperoxia)',     icon: Activity, done: () => false, timing: 'avoid hyperventilation' },
  { key: 'ttm',             label: 'Consider targeted temp management',        icon: ClipboardCheck, done: () => false, timing: 'per RI protocol' },
  { key: 'map',             label: 'Hemodynamic support (MAP ≥ 65)',           icon: Activity, done: () => false, timing: 'fluids / pressors PRN' },
  { key: 'transport',       label: 'Transport decision + notification',        icon: RotateCw, done: () => false, timing: 'closest appropriate ± PCI' },
];

// ============================================================
// H's & T's — reversible causes
//
// Each cause carries an in-the-moment ACTION (what to physically do
// right now) plus the rhythm branches where it's a top suspect. The
// algorithm panel surfaces a prioritized subset based on the current
// branch — non-shockable rhythms (PEA/Asystole) are nearly always
// driven by a reversible cause, so we lead with the high-yield ones
// for that branch (Hypoxia, Hypovolemia, Tension PTX, Toxins, K+).
// Shockable rhythms get a different priority (Hypoxia, K+, Acidosis,
// Thrombosis) since the underlying lesion is more often electrical.
// ============================================================
const REVERSIBLE_CAUSES = [
  { key: 'Hypoxia',              hint: 'Oxygenation — advanced airway, waveform EtCO2',         action: 'Confirm advanced airway · 100% O2 · capnography ≥ 10 mmHg', branches: ['shockable', 'non_shockable'], priority: { non_shockable: 1, shockable: 1 } },
  { key: 'Hypovolemia',          hint: 'Volume loss — bleeding, dehydration, third-spacing',    action: 'NS / LR bolus 1 L wide-open · stop external hemorrhage',      branches: ['non_shockable'],           priority: { non_shockable: 2, shockable: 6 } },
  { key: 'Hypo/Hyperkalemia',    hint: 'ESRD, dialysis miss, DKA, known K imbalance',           action: 'CaCl 1 g + bicarb 50 mEq IV (hyper-K) · contact med command', branches: ['shockable', 'non_shockable'], priority: { non_shockable: 4, shockable: 2 } },
  { key: 'Hydrogen Ion (Acidosis)', hint: 'Severe metabolic / respiratory acidosis',            action: 'Improve ventilation rate · sodium bicarb per med command',    branches: ['shockable', 'non_shockable'], priority: { non_shockable: 5, shockable: 3 } },
  { key: 'Tension Pneumothorax', hint: 'Trauma, asthma, PPV — needle decompress',               action: 'Needle decompression 2nd ICS MCL or 4th–5th ICS AAL',         branches: ['non_shockable'],           priority: { non_shockable: 3, shockable: 8 } },
  { key: 'Toxins',               hint: 'Overdose, ingestion — consider Narcan / bicarb / Ca',   action: 'Narcan 2 mg IN/IV · bicarb (TCA) · Ca (CCB/BB) · consult',    branches: ['shockable', 'non_shockable'], priority: { non_shockable: 6, shockable: 5 } },
  { key: 'Thrombosis (Coronary)',  hint: 'Post-ROSC 12-lead → STEMI → PCI',                     action: 'Continue ACLS · obtain post-ROSC 12-lead · PCI destination',  branches: ['shockable', 'rosc'],       priority: { shockable: 4, non_shockable: 8 } },
  { key: 'Thrombosis (Pulmonary)', hint: 'PE — consider fibrinolysis per med command',          action: 'Continue ACLS · contact med command for fibrinolysis',        branches: ['shockable', 'non_shockable'], priority: { non_shockable: 7, shockable: 7 } },
  { key: 'Tamponade (Cardiac)',  hint: 'Trauma, known pericardial effusion',                    action: 'Volume support · expedite transport for pericardiocentesis',  branches: ['non_shockable'],           priority: { non_shockable: 9, shockable: 9 } },
  { key: 'Hypothermia',          hint: 'Core temp, environmental exposure',                     action: 'Active rewarming · continue CPR until core temp > 32 °C',     branches: ['non_shockable'],           priority: { non_shockable: 10, shockable: 10 } },
] as const;

type CauseRow = typeof REVERSIBLE_CAUSES[number];

function prioritizedCauses(branch: 'shockable' | 'non_shockable' | 'rosc' | 'termination' | null): CauseRow[] {
  if (!branch || branch === 'termination') return [...REVERSIBLE_CAUSES];
  const key = branch === 'rosc' ? 'shockable' : branch;
  return [...REVERSIBLE_CAUSES].sort(
    (a, b) => (a.priority[key as 'shockable' | 'non_shockable'] ?? 99) - (b.priority[key as 'shockable' | 'non_shockable'] ?? 99),
  );
}

// ============================================================
// Component
// ============================================================
export function ACLSAlgorithmPanel() {
  const { events, session, logEvent } = useCode99Store();

  const lastRhythm = useMemo(() => {
    return events.find((e) => e.event_type === 'rhythm');
  }, [events]);

  const branch: 'shockable' | 'non_shockable' | 'rosc' | 'termination' | null = useMemo(() => {
    if (!lastRhythm) return null;
    const r = lastRhythm.details?.rhythm as Code99Rhythm | undefined;
    if (r === 'VF' || r === 'PVT') return 'shockable';
    if (r === 'PEA' || r === 'Asystole') return 'non_shockable';
    if (r === 'ROSC' || r === 'Organized' || hasROSC(events)) return 'rosc';
    return null;
  }, [lastRhythm, events]);

  // Termination criteria: asystole logged ≥ 20 min with at least 1 epi
  const terminationEligible = useMemo(() => {
    const firstAsys = events.slice().reverse().find(
      (e) => e.event_type === 'rhythm' && e.details?.rhythm === 'Asystole',
    );
    if (!firstAsys) return false;
    const elapsedMs = Date.now() - new Date(firstAsys.event_time).getTime();
    return elapsedMs >= 20 * 60_000 && epiCount(events) >= 1;
  }, [events]);

  const steps = branch === 'shockable' ? SHOCKABLE_BRANCH
    : branch === 'non_shockable' ? NON_SHOCKABLE_BRANCH
    : branch === 'rosc' ? ROSC_BRANCH
    : [];

  const currentStepIdx = steps.findIndex((s) => !s.done(events));

  const reversibleState = useMemo(() => {
    const map: Record<string, 'considered' | 'addressed' | 'ruled_out' | undefined> = {};
    for (const e of events) {
      if (e.event_type !== 'reversible_cause') continue;
      const cause = String(e.details?.cause ?? '');
      const status = e.details?.status as 'considered' | 'addressed' | 'ruled_out' | undefined;
      if (cause && status) map[cause] = status;
    }
    return map;
  }, [events]);

  async function markCause(cause: string, status: 'considered' | 'addressed' | 'ruled_out') {
    await logEvent({
      eventType: 'reversible_cause',
      details: { cause, status },
    });
  }

  return (
    <div className="space-y-3">
      {/* Branch header */}
      <div className="border border-slate-700 bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-800 px-2 py-1.5">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">
            ACLS Algorithm
          </span>
          <BranchBadge branch={branch} />
        </div>
        {!branch && (
          <div className="p-2.5 text-[11px] text-slate-500 italic leading-snug">
            Log a rhythm (tap <span className="text-slate-300 font-semibold">Rhythm</span> on the action bar) to see
            the ACLS branch. Until then, prioritize high-quality CPR and rhythm analysis.
          </div>
        )}

        {/* Step list */}
        {branch && (
          <ol className="divide-y divide-slate-800">
            {steps.map((step, idx) => {
              const done = step.done(events);
              const current = idx === currentStepIdx;
              return (
                <li key={step.key}
                    className={cn(
                      'flex items-start gap-2 px-2 py-1.5',
                      current && 'bg-amber-950/30',
                      done && 'opacity-75',
                    )}>
                  {done
                    ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                    : current
                      ? <Circle size={14} className="text-amber-300 fill-amber-400/30 shrink-0 mt-0.5" />
                      : <Circle size={14} className="text-slate-600 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className={cn(
                      'text-sm leading-tight',
                      done ? 'text-slate-400 line-through decoration-slate-600' :
                      current ? 'text-amber-100 font-semibold' :
                      'text-slate-200',
                    )}>
                      <step.icon size={12} className="inline mr-1 -mt-0.5" />
                      {step.label}
                    </div>
                    {step.timing && (
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                        {step.timing}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* Termination banner */}
      {terminationEligible && session?.disposition !== 'termination' && (
        <div className="border border-red-700 bg-red-950/40 p-2">
          <div className="flex items-start gap-2 text-red-200">
            <Skull size={14} className="shrink-0 mt-0.5 text-red-400" />
            <div className="flex-1 text-xs leading-snug">
              <div className="font-bold uppercase tracking-wider text-red-300">Termination Criteria Met</div>
              Asystole ≥ 20 min with at least one epinephrine dose. Consider medical command
              contact for termination of resuscitation if reversible causes addressed and
              protocol criteria confirmed. Tap <span className="font-semibold">Terminate</span>
              on the action bar to open the termination workflow.
            </div>
          </div>
        </div>
      )}

      {/* H's & T's — only useful in the moment when (a) a branch is
          active AND (b) the algorithm has reached the "treat
          reversible causes" beat. Before then it's just clutter. */}
      {branch && (
        <ReversibleCausesPanel
          branch={branch}
          reversibleState={reversibleState}
          markCause={markCause}
          isAlgorithmStep={
            // For non-shockable: reversible cause is step 6 (idx 5).
            // For shockable: surfaces at "CPR × 2 min + treat reversible
            // causes" (cpr_round_3, idx 8). Eagerly show after 2nd shock
            // since that's typically when crews start working through
            // the differential.
            (branch === 'non_shockable' && currentStepIdx >= 4) ||
            (branch === 'shockable' && shockCount(events) >= 2) ||
            branch === 'rosc'
          }
        />
      )}
    </div>
  );
}

function ReversibleCausesPanel({
  branch,
  reversibleState,
  markCause,
  isAlgorithmStep,
}: {
  branch: 'shockable' | 'non_shockable' | 'rosc' | 'termination';
  reversibleState: Record<string, 'considered' | 'addressed' | 'ruled_out' | undefined>;
  markCause: (cause: string, status: 'considered' | 'addressed' | 'ruled_out') => Promise<void>;
  isAlgorithmStep: boolean;
}) {
  const causes = useMemo(() => prioritizedCauses(branch), [branch]);
  const branchLabel =
    branch === 'shockable' ? 'shockable rhythm' :
    branch === 'non_shockable' ? 'PEA / asystole — high-yield first' :
    branch === 'rosc' ? 'post-ROSC differential' :
    'termination review';
  return (
    <div className={cn(
      'border bg-slate-900',
      isAlgorithmStep ? 'border-amber-600 ring-1 ring-amber-500/30' : 'border-slate-700',
    )}>
      <div className="flex items-center justify-between border-b border-slate-800 px-2 py-1.5">
        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300 flex items-center gap-1.5">
          <AlertTriangle size={11} className={isAlgorithmStep ? 'text-amber-400' : 'text-slate-500'} />
          Reversible Causes — {branchLabel}
        </span>
        <span className="text-[9px] font-mono text-slate-500">
          tap to address
        </span>
      </div>
      {isAlgorithmStep && (
        <div className="px-2 py-1.5 text-[10px] text-amber-200 bg-amber-950/30 border-b border-amber-900/60 leading-snug">
          Algorithm is at the differential beat. Work the prioritized list — top causes are the ones most likely to flip this rhythm.
        </div>
      )}
      <ul className="divide-y divide-slate-800">
        {causes.map((c, i) => {
          const state = reversibleState[c.key];
          const isTopThree = i < 3;
          return (
            <li key={c.key} className={cn(
              'flex items-start gap-2 px-2 py-1.5',
              isTopThree && state === undefined && isAlgorithmStep && 'bg-slate-900/40',
            )}>
              <button
                onClick={() => {
                  const next = state === undefined ? 'considered'
                    : state === 'considered' ? 'addressed'
                    : state === 'addressed' ? 'ruled_out'
                    : 'considered';
                  void markCause(c.key, next);
                }}
                className={cn(
                  'shrink-0 flex items-center justify-center h-7 w-7 border text-[10px] font-bold uppercase rounded',
                  state === 'addressed'  ? 'border-emerald-500 bg-emerald-600 text-white' :
                  state === 'considered' ? 'border-amber-500 bg-amber-600 text-white' :
                  state === 'ruled_out'  ? 'border-slate-600 bg-slate-700 text-slate-300 line-through' :
                  isTopThree && isAlgorithmStep
                    ? 'border-amber-500 bg-slate-800 text-amber-300 hover:bg-amber-950/40'
                    : 'border-slate-600 bg-slate-800 text-slate-500 hover:text-slate-200',
                )}
                title={
                  state === 'addressed' ? 'Addressed — tap to mark ruled out' :
                  state === 'considered' ? 'Considered — tap to mark addressed' :
                  state === 'ruled_out' ? 'Ruled out — tap to reset to considered' :
                  'Tap to mark considered'
                }
              >
                {state === 'addressed' ? '✓' :
                 state === 'considered' ? '!' :
                 state === 'ruled_out' ? '–' :
                 <Plus size={12} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1.5">
                  {isTopThree && state === undefined && isAlgorithmStep && (
                    <span className="text-[9px] font-mono font-bold text-amber-400 tabular-nums">#{i + 1}</span>
                  )}
                  <div className={cn(
                    'text-xs font-semibold',
                    state === 'addressed' ? 'text-emerald-300' :
                    state === 'considered' ? 'text-amber-300' :
                    state === 'ruled_out' ? 'text-slate-500 line-through' :
                    'text-slate-200',
                  )}>
                    {c.key}
                  </div>
                </div>
                <div className="text-[10px] text-slate-400 leading-snug">{c.hint}</div>
                {(isAlgorithmStep || state === 'considered') && (
                  <div className="text-[10px] text-blue-300 leading-snug mt-0.5 font-medium">
                    → {c.action}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function BranchBadge({ branch }: { branch: 'shockable' | 'non_shockable' | 'rosc' | 'termination' | null }) {
  if (!branch) {
    return <span className="status-pill bg-slate-800 text-slate-500 border border-slate-700">waiting</span>;
  }
  const cls = {
    shockable:      'bg-amber-950 text-amber-300 border border-amber-700',
    non_shockable:  'bg-blue-950  text-blue-300  border border-blue-700',
    rosc:           'bg-emerald-950 text-emerald-300 border border-emerald-700',
    termination:    'bg-red-950 text-red-300 border border-red-700',
  }[branch];
  const label = {
    shockable:     'Shockable',
    non_shockable: 'Non-Shockable',
    rosc:          'Post-ROSC',
    termination:   'Termination',
  }[branch];
  return <span className={`status-pill ${cls}`}>{label}</span>;
}
